import { ArrowLeft } from 'shared/icons';
import { Mono } from '../../ui/Mono';

/**
 * TECLADO NUMÉRICO DA BANCADA.
 *
 * O operador da máquina está de luva, em pé, e o número que ele digita (kits,
 * quantidade, refugo) é o dado mais importante do apontamento. Um `<input
 * type="number">` ali significa teclado virtual em cima da tela, setinhas de
 * 12 px e a chance de trocar 18 por 180 sem perceber.
 *
 * Então o número é montado por TOQUE, com alvos grandes, e aparece gigante em
 * cima — o mesmo princípio das telas do coletor (o dado em tamanho de leitura à
 * distância, a instrução subordinada).
 *
 * Decimal existe porque o saldo é DECIMAL(18,6) no banco (bobina se mede em
 * metro/quilo, não em peça): a vírgula é a do teclado brasileiro e vira ponto
 * só na hora de virar número.
 */
const KEYS = ['7', '8', '9', '4', '5', '6', '1', '2', '3', ',', '0'];

const BenchNumberPad = ({ label, value, onChange, allowDecimal = true, suffix = null, hint = null, disabled = false }) => {
    const press = (key) => {
        if (disabled) {
            return;
        }
        if (key === ',') {
            if (!allowDecimal || value.includes(',')) {
                return;
            }
            onChange(value === '' ? '0,' : `${value},`);
            return;
        }
        // Zero à esquerda não vira "007": o campo começa vazio e o primeiro
        // dígito substitui o zero solitário.
        onChange(value === '0' ? key : `${value}${key}`);
    };

    const backspace = () => {
        if (!disabled) {
            onChange(value.slice(0, -1));
        }
    };

    const clear = () => {
        if (!disabled) {
            onChange('');
        }
    };

    const buttonClass =
        'flex items-center justify-center rounded-xl border-2 border-mono-300 bg-white py-5 text-3xl font-black text-black transition-colors hover:bg-mono-50 active:bg-mono-100 disabled:opacity-40 dark:border-mono-700 dark:bg-mono-950 dark:text-white dark:hover:bg-mono-900';

    return (
        <div className="space-y-3">
            <div className="rounded-2xl border-2 border-mono-300 bg-white px-4 py-5 text-center dark:border-mono-700 dark:bg-mono-950">
                <Mono className="block break-all text-5xl font-black text-black dark:text-white">
                    {value === '' ? '0' : value}
                    {suffix ? <span className="ml-2 text-2xl text-mono-500">{suffix}</span> : null}
                </Mono>
            </div>
            {hint ? <p className="text-center text-sm text-mono-600 dark:text-mono-400">{hint}</p> : null}

            <div className="grid grid-cols-3 gap-2">
                {KEYS.map((key) => (
                    <button
                        key={key}
                        type="button"
                        onClick={() => press(key)}
                        disabled={disabled || (key === ',' && !allowDecimal)}
                        className={buttonClass}
                    >
                        {key}
                    </button>
                ))}
                <button
                    type="button"
                    onClick={backspace}
                    disabled={disabled}
                    className={buttonClass}
                    aria-label="Apagar"
                >
                    <ArrowLeft size={28} className="mx-auto" />
                </button>
            </div>

            <button
                type="button"
                onClick={clear}
                disabled={disabled}
                className="w-full rounded-xl border-2 border-mono-300 py-3 text-lg font-semibold text-mono-600 transition-colors hover:bg-mono-50 disabled:opacity-40 dark:border-mono-700 dark:text-mono-300 dark:hover:bg-mono-900"
            >
                {label('wms.bench.keypad_clear')}
            </button>
        </div>
    );
};

/** `"12,5"` → `12.5`. Devolve `null` quando não é número (campo vazio inclusive). */
export function ParsePadValue(raw) {
    const text = String(raw ?? '').replace(',', '.');
    if (text.trim() === '') {
        return null;
    }
    const number = Number(text);
    return Number.isFinite(number) ? number : null;
}

export default BenchNumberPad;
